import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';

import { catchError, map, switchMap, tap } from 'rxjs/operators';
import { of } from 'rxjs';

import { Apollo, gql } from 'apollo-angular';

import { Translations } from '@ui/shared/model';
import { I18nService } from '../../i18n/i18n.service';
import {
  CHECK_TRANSLATIONS,
  CheckTranslations,
  LOAD_TRANSLATION,
  LOAD_TRANSLATION_FAIL,
  LoadDefaultTranslation,
  LoadTranslation,
  LoadTranslationFail,
  LoadTranslationSuccess,
  REMOVE_TRANSLATIONS
} from './translation.actions';

const translationQuery = gql`
  query translations(
    $appName: String!
    $langCode: String!
    $informal: Boolean!
  ) {
    translations(appName: $appName, langCode: $langCode, informal: $informal)
  }
`;

interface TranslationsResponse {
  translations: Translations;
}

@Injectable()
export class TranslationEffects {
  private apollo = inject(Apollo);
  private actions$ = inject(Actions);
  private i18nService = inject(I18nService);

  checkTranslations$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CHECK_TRANSLATIONS),
      map((action: CheckTranslations) => {
        return new LoadTranslation(
          action.appName,
          action.langCode,
          action.informal
        );
      }),
      catchError(() => [new LoadDefaultTranslation()])
    )
  );

  loadTranslation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LOAD_TRANSLATION),
      switchMap((action: LoadTranslation) => {
        return this.apollo
          .query<TranslationsResponse>({
            query: translationQuery,
            variables: {
              appName: action.appName,
              langCode: action.langCode,
              informal: action.informal
            }
          })
          .pipe(
            map(response => {
              const translations = response.data && response.data.translations;

              if (translations && translations[action.langCode]) {
                this.i18nService.setTranslations(
                  action.langCode,
                  action.informal,
                  translations
                );
                return new LoadTranslationSuccess(
                  action.langCode,
                  action.informal
                );
              }

              return new LoadTranslationFail('UNEXPECTED_DATA');
            }),
            catchError(error => of(new LoadTranslationFail(error)))
          );
      })
    )
  );

  loadTranslationFail$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LOAD_TRANSLATION_FAIL),
      switchMap((action: LoadTranslationFail) => {
        console.error(`Error loading translation: ${action.error}`);
        return of(new LoadDefaultTranslation());
      })
    )
  );

  removeTranslations$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(REMOVE_TRANSLATIONS),
        tap(() => this.i18nService.removeTranslations())
      ),
    { dispatch: false }
  );
}
